import React, { useState, useEffect, useRef } from "react";

import "./StatisticGraphicsModal.css";

import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Chart as PrimeChart } from "primereact/chart";

import { getLayerAttributes } from "../../../../_config/layers/zcm";

import "chart.js/auto";

const StatisticGraphicsModal = ({ active, callback, map }) => {
  const { t } = useTranslation();

  const chartRef = useRef(null);

  const layers = [...map.getLayers().getArray()];

  /* Opções dos dropdowns */
  const [layerOptions, setLayerOptions] = useState([]);
  const [fieldXOptions, setFieldXOptions] = useState([]);
  const [fieldYOptions, setFieldYOptions] = useState([]);

  /* Valores selecionados */
  const [layerName, setLayerName] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState("line");
  const [fieldX, setFieldX] = useState(null);
  const [fieldY, setFieldY] = useState(null);

  /* Gráfico renderizado */
  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);
  const [chartType, setChartType] = useState("bar");
  const [loading, setLoading] = useState(false);

  const chartTypeOptions = [
    {
      label: t("statisticModal.chartType.line"),
      value: "line",
      icon: "pi pi-chart-line",
    },
    {
      label: t("statisticModal.chartType.bar"),
      value: "bar",
      icon: "pi pi-chart-bar",
    },
    {
      label: t("statisticModal.chartType.pie"),
      value: "pie",
      icon: "pi pi-chart-pie",
    },
  ];

  const handleClose = () => {
    callback(false);
  };

  useEffect(() => {
    if (active) {
      buildLayerSelect();
    }
  }, [active]);

  /* =========================================================
     LAYERS
  ========================================================= */

  function buildLayerSelect() {
    setLayerOptions(
      layers
        .filter((layer) => layer.get("isAllowedToStatistics"))
        .map((layer) => ({
          label: layer.get("title"),
          value: layer.get("wmsName").split(":")[1],
        }))
    );
  }

  function handleLayerChange(value) {
    setLayerName(value);

    setFieldX(null);
    setFieldY(null);
    setFieldXOptions([]);
    setFieldYOptions([]);

    if (value) {
      loadAttributes(value);
    }
  }

  /* =========================================================
     ATRIBUTOS
  ========================================================= */

  async function loadAttributes(layer) {
    try {
      setLoading(true);

      const { wfsAttributes } = await getLayerAttributes(layer);

      const attributes = wfsAttributes
        .match(/[\w]+:/g)
        .map((attribute) => attribute.slice(0, -1));

      const types = wfsAttributes
        .match(/[\w]+,/g)
        .map((type) => type.slice(0, -1));

      populateAttributes(attributes, types);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  function populateAttributes(attrs, types) {
    const optionsX = [];
    const optionsY = [];

    attrs.forEach((attr, index) => {
      if (isTextualOrDate(types[index]) || attr.includes("ano")) {
        optionsX.push({ label: attr, value: attr });
      }

      if (isNumericOrDate(types[index])) {
        optionsY.push({ label: attr, value: attr });
      }
    });

    setFieldXOptions(optionsX);
    setFieldYOptions(optionsY);
  }

  const isTextualOrDate = (type) =>
    [
      "character",
      "varchar",
      "text",
      "date",
      "timestamp",
      "time",
      "string",
    ].some((item) => type.startsWith(item));

  const isNumericOrDate = (type) =>
    [
      "integer",
      "int",
      "float",
      "double",
      "bigdecimal",
      "long",
      "date",
    ].some((item) => type.startsWith(item));

  /* =========================================================
     CHART
  ========================================================= */

  async function generateChart(event) {
    event.preventDefault();

    if (!layerName || !fieldX || !fieldY) {
      return;
    }

    const type = selectedChartType;

    setChartType(type);
    setLoading(true);

    const url =
      `https://hgpedea.sema.ce.gov.br/geoserver/wfs` +
      `?service=WFS` +
      `&version=1.0.0` +
      `&request=GetFeature` +
      `&typeName=${layerName}` +
      `&outputFormat=application/json`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      const x = [];
      const y = [];

      data.features.forEach((feature) => {
        x.push(feature.properties[fieldX]);
        y.push(feature.properties[fieldY]);
      });

      const agg = aggregateData(x, y);

      renderChart(type, agg.xValues, agg.yValues, fieldX, fieldY);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  function aggregateData(x, y) {
    const acc = {};

    x.forEach((value, index) => {
      acc[value] = (acc[value] || 0) + y[index];
    });

    return {
      xValues: Object.keys(acc),
      yValues: Object.values(acc),
    };
  }

  function renderChart(type, labels, values, xField, yField) {
    const semaColors = [
      "#0e4b3a",
      "#12654e",
      "#2d8a70",
      "#a9762f",
      "#d3a05a",
    ];

    setChartData({
      labels,

      datasets: [
        {
          label: `${yField} por ${xField}`,
          data: values,
          backgroundColor: semaColors,
          borderColor: semaColors,
          borderWidth: 1,
          tension: type === "line" ? 0.4 : 0,
        },
      ],
    });

    setChartOptions({
      responsive: true,
      maintainAspectRatio: false,

      plugins: {
        legend: {
          position: "top",

          labels: {
            font: {
              family: "Space Grotesk",
              size: 13,
              weight: "600",
            },
          },
        },

        tooltip: {
          padding: 12,
        },
      },

      scales:
        type === "pie"
          ? {}
          : {
              x: {
                ticks: {
                  maxRotation: 45,
                  minRotation: 45,
                },
              },

              y: {
                beginAtZero: true,
              },
            },
    });
  }

  /* =========================================================
     EXPORT
  ========================================================= */

  function exportChart() {
    if (!chartRef.current) return;

    const chart =
      chartRef.current.chart || chartRef.current.getChart?.();

    if (!chart) return;

    const imageBase64 = chart.toBase64Image("image/png", 1);

    const temp = document.createElement("canvas");

    temp.width = chart.width + 40;
    temp.height = chart.height + 80;

    const ctx = temp.getContext("2d");

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, temp.width, temp.height);

    ctx.fillStyle = "#000";
    ctx.font = "12px Arial";

    const layerTitle =
      layerOptions.find((option) => option.value === layerName)?.label || "";

    ctx.fillText(
      `Fonte: PEDEA SEMA/CE | Camada: ${layerTitle}`,
      10,
      20
    );

    const img = new Image();

    img.src = imageBase64;

    img.onload = () => {
      ctx.drawImage(img, 20, 40);

      const link = document.createElement("a");

      link.href = temp.toDataURL("image/png");
      link.download = "grafico.png";

      link.click();
    };
  }

  /* =========================================================
     TEMPLATES DOS DROPDOWNS
  ========================================================= */

  const chartTypeItemTemplate = (option) => {
    if (!option) return null;

    return (
      <div className="StatisticGraphicsOption">
        <i className={option.icon} />
        <span>{option.label}</span>
      </div>
    );
  };

  const chartTypeValueTemplate = (option, props) => {
    if (!option) return <span>{props.placeholder}</span>;

    return chartTypeItemTemplate(option);
  };

  const footer = (
    <div className="StatisticGraphicsFooter">
      <Button
        onClick={handleClose}
        severity="secondary"
        icon="pi pi-times"
        label={t("common.close")}
        className="CloseBtn"
      />

      <Button
        id="exportBtn"
        icon="pi pi-file-export"
        label={t("statisticModal.exportButton")}
        onClick={exportChart}
        disabled={!chartData}
        className="StatisticGraphicsExportButton"
      />
    </div>
  );

  const canGenerate = !!layerName && !!fieldX && !!fieldY && !loading;

  return (
    <Dialog
      className="StatisticGraphicsDialog"
      header={t("statisticModal.title")}
      visible={active}
      onHide={handleClose}
      modal
      dismissableMask
      closable
      appendTo={document.body}
      draggable={false}
      resizable={false}
      blockScroll
      footer={footer}
      style={{ width: "min(1100px, 94vw)" }}
    >
      <div className="StatisticGraphicsBody">
        <form className="StatisticGraphicsForm" onSubmit={generateChart}>
          <div className="StatisticGraphicsFields">
            <div className="StatisticGraphicsField StatisticGraphicsFieldLayer">
              <label htmlFor="layerName">
                {t("statisticModal.layerLabel")}
              </label>

              <Dropdown
                inputId="layerName"
                className="StatisticGraphicsDropdown"
                panelClassName="StatisticGraphicsPanel"
                value={layerName}
                options={layerOptions}
                onChange={(e) => handleLayerChange(e.value)}
                placeholder={t("statisticModal.layerPlaceholder")}
                filter
                filterPlaceholder="Buscar camada..."
                emptyMessage="Nenhuma camada disponível"
                emptyFilterMessage="Nenhuma camada encontrada"
                showClear
                appendTo={document.body}
              />
            </div>

            <div className="StatisticGraphicsField">
              <label htmlFor="chartType">
                {t("statisticModal.chartTypeLabel")}
              </label>

              <Dropdown
                inputId="chartType"
                className="StatisticGraphicsDropdown"
                panelClassName="StatisticGraphicsPanel"
                value={selectedChartType}
                options={chartTypeOptions}
                onChange={(e) => setSelectedChartType(e.value)}
                itemTemplate={chartTypeItemTemplate}
                valueTemplate={chartTypeValueTemplate}
                appendTo={document.body}
              />
            </div>

            <div className="StatisticGraphicsField">
              <label htmlFor="fieldX">
                {t("statisticModal.xFieldLabel")}
              </label>

              <Dropdown
                inputId="fieldX"
                className="StatisticGraphicsDropdown"
                panelClassName="StatisticGraphicsPanel"
                value={fieldX}
                options={fieldXOptions}
                onChange={(e) => setFieldX(e.value)}
                placeholder="Selecione um campo..."
                emptyMessage="Selecione uma camada primeiro"
                filter={fieldXOptions.length > 8}
                disabled={!layerName || fieldXOptions.length === 0}
                appendTo={document.body}
              />
            </div>

            <div className="StatisticGraphicsField">
              <label htmlFor="fieldY">
                {t("statisticModal.yFieldLabel")}
              </label>

              <Dropdown
                inputId="fieldY"
                className="StatisticGraphicsDropdown"
                panelClassName="StatisticGraphicsPanel"
                value={fieldY}
                options={fieldYOptions}
                onChange={(e) => setFieldY(e.value)}
                placeholder="Selecione um campo..."
                emptyMessage="Selecione uma camada primeiro"
                filter={fieldYOptions.length > 8}
                disabled={!layerName || fieldYOptions.length === 0}
                appendTo={document.body}
              />
            </div>
          </div>

          <Button
            type="submit"
            icon="pi pi-chart-bar"
            label={t("statisticModal.generateButton")}
            className="StatisticGraphicsGenerateButton"
            disabled={!canGenerate}
          />
        </form>

        {loading && (
          <div className="StatisticGraphicsLoader">
            <i className="pi pi-spin pi-spinner" />
            <span>Carregando...</span>
          </div>
        )}

        <div className="StatisticGraphicsChart">
          {chartData && (
            <PrimeChart
              ref={chartRef}
              type={chartType}
              data={chartData}
              options={chartOptions}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          )}

          {!chartData && !loading && (
            <div className="StatisticGraphicsEmpty">
              <i className="pi pi-chart-bar" />

              <span>Configure os campos e gere um gráfico.</span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default StatisticGraphicsModal;